/**
 * A/B access comparison for User Lookup security map.
 */
import { useMemo, useState } from 'react';
import { GitCompare, Loader2, AlertCircle } from 'lucide-react';
import { compareSecurityUsers } from './securityAccessMapApi';
import { UserSearchCombobox, ACCENT } from './UserSecurityAccessMap';

function CompareSectionTable({ title, onlyA, onlyB, both, labelA, labelB }) {
  const rows = useMemo(() => {
    const list = [];
    for (const item of onlyA || []) list.push({ ...item, side: 'a' });
    for (const item of both || []) list.push({ ...item, side: 'both' });
    for (const item of onlyB || []) list.push({ ...item, side: 'b' });
    return list.sort((x, y) => (x.name || '').localeCompare(y.name || ''));
  }, [onlyA, onlyB, both]);

  if (!rows.length) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-[11px] text-slate-500">
        No {title.toLowerCase()} rows to compare.
      </p>
    );
  }

  const rowClass = (side) => {
    if (side === 'both') return 'bg-emerald-50/80';
    if (side === 'a') return 'bg-amber-50/80';
    return 'bg-sky-50/80';
  };

  const badge = (side) => {
    if (side === 'both') return { text: 'Both', className: 'bg-emerald-100 text-emerald-800' };
    if (side === 'a') return { text: labelA, className: 'bg-amber-100 text-amber-900' };
    return { text: labelB, className: 'bg-sky-100 text-sky-900' };
  };

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <div className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
        {title}
      </div>
      <div className="max-h-48 overflow-y-auto">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1.5 font-medium">Access</th>
              <th className="px-2 py-1.5 font-medium">Category</th>
              <th className="w-20 px-2 py-1.5 font-medium">Side</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const b = badge(row.side);
              return (
                <tr
                  key={`${row.key}-${row.side}`}
                  className={`border-t border-slate-100 ${rowClass(row.side)}`}
                >
                  <td className="px-2 py-1.5 font-medium text-slate-800">{row.name}</td>
                  <td className="px-2 py-1.5 text-slate-600">
                    {String(row.category || '').replace(/_/g, ' ')}
                  </td>
                  <td className="px-2 py-1.5">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${b.className}`}
                    >
                      {b.text}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function shortUserLabel(users, username) {
  const u = users.find((x) => x.username === username);
  if (!u) return username || 'User';
  return u.displayName ? u.displayName.split(' ')[0] : username;
}

export default function UserAccessCompare({
  pickerUsers = [],
  initialUserA = '',
  initialUserB = '',
  useExtended = false,
  accent = 'cyan',
}) {
  const [userA, setUserA] = useState(initialUserA || '');
  const [userB, setUserB] = useState(initialUserB || '');
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [elapsedMs, setElapsedMs] = useState(null);

  const accentStyles = ACCENT[accent] || ACCENT.cyan;
  const labelA = shortUserLabel(pickerUsers, userA) || 'A';
  const labelB = shortUserLabel(pickerUsers, userB) || 'B';

  const clearCompare = () => {
    setUserA('');
    setUserB('');
    setDiff(null);
    setError('');
    setElapsedMs(null);
  };

  const hasCompareData = !!(diff || error || userA || userB || elapsedMs != null);

  const runCompare = async () => {
    if (!userA || !userB) {
      setError('Select both users.');
      return;
    }
    if (userA.toLowerCase() === userB.toLowerCase()) {
      setError('Choose two different users.');
      return;
    }
    setLoading(true);
    setError('');
    setDiff(null);
    try {
      const result = await compareSecurityUsers({
        usernameA: userA,
        usernameB: userB,
        useExtended,
      });
      setDiff(result.diff);
      setElapsedMs(result.elapsedMs);
    } catch (err) {
      setError(err.message || 'Compare failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          User A
          <UserSearchCombobox
            users={pickerUsers}
            value={userA}
            onChange={setUserA}
            accentStyles={accentStyles}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          User B
          <UserSearchCombobox
            users={pickerUsers}
            value={userB}
            onChange={setUserB}
            accentStyles={accentStyles}
          />
        </label>
        <button
          type="button"
          onClick={runCompare}
          disabled={loading || !pickerUsers.length}
          className="inline-flex items-center gap-1.5 rounded-md bg-violet-700 px-3 py-2 text-xs font-medium text-white hover:bg-violet-800 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitCompare className="h-3.5 w-3.5" />}
          Run comparison
        </button>
        <button
          type="button"
          onClick={clearCompare}
          disabled={loading || !hasCompareData}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Clear
        </button>
        {elapsedMs != null ? (
          <span className="text-[10px] text-slate-400">{elapsedMs} ms</span>
        ) : null}
      </div>

      {error ? (
        <div className="flex items-start gap-2 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </div>
      ) : null}

      {diff ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <CompareSectionTable
            title="Assigned roles"
            onlyA={diff.rolesOnlyA}
            onlyB={diff.rolesOnlyB}
            both={diff.rolesBoth}
            labelA={labelA}
            labelB={labelB}
          />
          <CompareSectionTable
            title="Privileges"
            onlyA={diff.privilegesOnlyA}
            onlyB={diff.privilegesOnlyB}
            both={diff.privilegesBoth}
            labelA={labelA}
            labelB={labelB}
          />
          <CompareSectionTable
            title="Data security"
            onlyA={diff.dataSecurityOnlyA}
            onlyB={diff.dataSecurityOnlyB}
            both={diff.dataSecurityBoth}
            labelA={labelA}
            labelB={labelB}
          />
          <CompareSectionTable
            title="Person scope"
            onlyA={diff.personScopeOnlyA}
            onlyB={diff.personScopeOnlyB}
            both={diff.personScopeBoth}
            labelA={labelA}
            labelB={labelB}
          />
        </div>
      ) : null}
    </div>
  );
}
